/**
 * The Atlas drawn as the world under someone else's camera: the external-world
 * switch cruise flips on the walkaround's own draw state, the body cruise
 * names as its dominant pull, the re-host that moves the mount's DOM between
 * the menu pane and the boot host, and the `atlasWorld` API `src/main.ts` and
 * `src/sim/landVerb.ts` drive it through.
 */

import * as THREE from 'three';

import type { WorldVec } from '../world/frame.ts';
import type { AtlasState, AtlasLive } from '../atlas';
import { bodyFixedQuaternion, eclipticToScene, type OrreryHandle } from './orrery.ts';
import { gameClock } from '../world/clock.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { GlobeMaterialHandle } from './globeMaterial.ts';
import type { ZoomLadderHandle } from './zoomLadder.ts';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { InputHandle } from './input.ts';
import type { MountHandle } from './mount.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { InjectsHandle } from './injects.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { NavigationHandle } from './navigation.ts';
import type { FarFieldHandle } from './farField.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { GroundColourHandle } from './groundColour.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { BodySwitchHandle } from './bodySwitch.ts';

/**
 * Ecliptic axes to the Atlas's, as a rotation on the subtree.
 *
 * The sim's own world frame is the propagator's ecliptic metres laid straight
 * onto the scene; everything under here is drawn through `eclipticToScene`,
 * which is that same frame turned a quarter turn about x. So the subtree
 * carries the quarter turn back while the sim is the side drawing, and the
 * camera's place is handed in through `eclipticToScene` to match.
 */
const EXTERNAL_TILT = Math.PI / 2;
const WORLD_SHAPE_MIN_DEG = 1;

/**
 * The world the sim flies in.
 *
 * Cruise draws the Atlas's own bodies, sky and sun around the ship: the
 * subtree stays attached while the sim's composer is the one drawing, and
 * the pose comes in from the chase camera each frame instead of the focus
 * rig. `src/main.ts` owns the mount and the handover; everything the ladder
 * needs - the tiles, the plot, the scattering shell, the cascades - is the
 * Atlas's own frame and is not touched here.
 *
 * Built before this module exists, since factories called earlier in the
 * Atlas's closure read these fields too; a plain mutable record rather than
 * a handle's getters, so a write from any side is seen on every other.
 */
export interface WorldHostState {
  externalWorld: boolean;
  externalCamWorld: WorldVec;
  /**
   * The fov and the drawn height cruise hands in, for the traversal's own ruler.
   *
   * The pane is display:none out here, so `root.clientHeight` is 0 and the
   * Atlas camera's fov is the focus rig's rather than the chase rig's; both
   * would put the tile tree's screen-space error on a viewport nobody is
   * looking at. Written by `atlasWorld.update` from the drawn camera.
   */
  externalFovDeg: number;
  /** Wall clock behind the cruise traversal, since cruise hands in no delta. */
  externalTileMs: number;
  /** True while the pyramid is drawn for a cruise camera, for the hysteresis below. */
  cruiseGroundOn: boolean;
  /** The height that gate last read, metres, or null while it reads none. */
  cruiseGroundHeightM: number | null;
  /** The body cruise draws in full, so a change is a mount and not a poll. */
  worldBodyId: string | null;
  /** The body the ship is at, so the Solar System view opens where the ship is. */
  shipFocusId: string | null;
  /**
   * The instant the world is drawn at while cruise owns the camera.
   *
   * The ship's own heliocentric state is propagated to ONE epoch and held there
   * (`src/main.ts`, `seedShipOrbit`), and the flight step integrates against
   * body centres that do not move. A world drawn at the live clock would take
   * Mars off the bow at 24 km/s while the ship did not follow, so in cruise the
   * bodies are drawn at the ship's own epoch and the Atlas's own views keep the
   * clock. Null is the clock.
   */
  worldEpochJd: number | null;
  /** Whether cruise draws the ship's reticle; see `setShipMarkerVisible`. */
  cruiseShipMarker: boolean;
}

export interface WorldHostDeps {
  readonly root: HTMLElement;
  readonly bootHost: HTMLElement;
  readonly state: AtlasState;
  readonly world: WorldHostState;
  own(fn: () => void): void;

  readonly mount: MountHandle;
  readonly plotMount: PlotMountHandle;
  readonly injects: InjectsHandle;
  readonly focusRig: FocusRigHandle;
  readonly navigation: NavigationHandle;
  readonly farField: FarFieldHandle;
  readonly plotSun: PlotSunHandle;
  readonly groundColour: GroundColourHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly bodyScene: BodySceneHandle;
  readonly bodySwitch: BodySwitchHandle;
  readonly live: AtlasLive;
  readonly cameraRig: CameraRigHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly ladder: ZoomLadderHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly dojoPaints: DojoPaintsHandle;
  readonly input: InputHandle;
}

export interface WorldHostHandle {
  /** A registry `#rrggbb` art colour as linear rgb, the space the plot mixes in. */
  linearHex(hex: string): [number, number, number] | null;
  /**
   * The world, drawn for a camera the Atlas does not own.
   *
   * `src/main.ts` mounts the Atlas once at boot and hands the chase camera's
   * pose in each frame of cruise; the Solar System view and the land verb hand
   * the camera back to the focus rig by switching this off.
   */
  setExternalWorld(on: boolean): void;
  /** A shape mesh for every body drawn large enough to show one. */
  requestWorldShapes(): void;
  /**
   * The body cruise draws in full: its air, its rings and its own shape.
   *
   * The scattering shell and the shape mesh are the Atlas's, mounted for the
   * body its own ladder is on, so cruise names that body rather than running
   * the focus rig at it. This moves `state.bodyId` and the globe the chunks
   * hang on and nothing else - no camera, no tier, no tile. `look` is how
   * much of the body's own air and exposure is drawn over the orrery's globe.
   */
  setWorldBody(id: string | null, look?: number): void;
  /** Re-host this mount into another element, and put it back on teardown. */
  adopt(pane: HTMLElement): () => void;
}

export function createWorldHost(deps: WorldHostDeps): WorldHostHandle {
  const {
    root, bootHost, state, world, own,
    cameraRig, pyramidView, globeMaterial, ladder,
    atmosphereView, bodyReaders, surfaceRadius, dojoPaints, input,
  } = deps;
  const { scene, atlasRoot, camera, cameraWorld, resize, subtree, worldFrame } = deps.mount;
  const { dtmGroup } = deps.plotMount;
  const { GROUNDED_RETURN_OLD } = deps.injects.state;
  const { fitOpening, focusCam } = deps.focusRig;
  const { syncTierToAltitude, applyState } = deps.navigation;
  const { placeFarField } = deps.farField;
  const { readCameraWorld } = cameraRig;
  const { setSunScale } = deps.plotSun;
  const { srgbToLinear } = deps.groundColour;
  const { descentQueue } = deps.descentLoad;
  const { externalCamWorld } = world;
  /**
   * Every body cruise has asked a shape mesh for.
   *
   * Not the dominant body's alone: a moon seen from close up is never the
   * hardest pull over its own primary, and Michael's rule is that a body with
   * a shape shows it at every zoom. The gate is how much of the sky the body
   * covers, which is the only distance at which the shape is visible at all.
   */
  const worldShapes = new Set<string>();
  const _extRootQ = new THREE.Quaternion();
  const _extCamQ = new THREE.Quaternion();

  /**
   * The camera, for the instruments.
   *
   * Hung off the Atlas's own scene beside the governor, for the same reason:
   * `descent-dojo.mjs` reaches the scene through three's devtools observe
   * events, so an altitude driver needs no global and nothing in the app has to
   * know the instrument exists. Everything here is a read or a camera move a
   * player could make with the wheel.
   */
  /** A registry `#rrggbb` art colour as linear rgb, the space the plot mixes in. */
  function linearHex(hex: string): [number, number, number] | null {
    const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1]!, 16);
    return [
      +srgbToLinear((n >> 16) & 255).toFixed(5),
      +srgbToLinear((n >> 8) & 255).toFixed(5),
      +srgbToLinear(n & 255).toFixed(5),
    ];
  }

  /**
   * The world, drawn for a camera the Atlas does not own.
   *
   * `src/main.ts` mounts the Atlas once at boot and hands the chase camera's
   * pose in each frame of cruise; the Solar System view and the land verb hand
   * the camera back to the focus rig by switching this off.
   */
  function setExternalWorld(on: boolean): void {
    if (world.externalWorld === on) return;
    world.externalWorld = on;
    // The tilt is head orientation, not drawn world state: crossing either way -
    // leaving the walkaround (Return to Ship, LAUNCH) or landing back into it -
    // opens the next stand looking level, never carrying a stale tip from the
    // stand before. `tick()` never runs to clear it while this flag is up.
    // `descent-dojo --lookup`'s `look-reset` skips this with `skipReturnTiltReset`.
    if (!cameraRig.lookUpDebug.skipReturnTiltReset) cameraRig.plotTilt = 0;
    world.worldBodyId = null;
    // The Atlas's own views always draw a mounted body's air in full.
    atmosphereView.setLook(1);
    subtree.setCoDraw(on);
    // The cruise traversal's own state: the gate starts closed either way, and
    // the first frame on the other side is not a frame's worth of delta.
    world.cruiseGroundOn = false;
    world.cruiseGroundHeightM = null;
    world.externalTileMs = 0;
    if (on) {
      input.held.clear();
      // The camera the ladder's resident set was chosen for has gone, and the
      // pane can close mid-stream: release it here rather than leave it frozen.
      // Cruise picks its own set from the traversal below, over the globe.
      pyramidView.releaseTiles();
      // A claim read outstanding when the pane closes would otherwise hold the
      // fetch gate down under the ship for up to CLAIM_HOLD_CEILING_MS.
      descentQueue.drain();
      // And the walkaround's own drawn state, which nothing else puts back:
      // the tick that eases the blend and lays the world root's matrix down
      // does not run out here. A root left carrying `placeFarField`'s basis
      // draws every body inside the plot's local frame while cruise draws the
      // air about the body's true centre - the globe and the shell about two
      // different centres. The mesh itself is kept, so a wheel back down to
      // the same ground mounts it again without a rebuild.
      if (!GROUNDED_RETURN_OLD) {
        dtmGroup.visible = false;
        // A rover follow left running here would otherwise survive to the
        // next LAND on this body, since the crossing that would end it
        // never runs while cruise, not the ladder, owns the frame.
        ladder.suspend();
        placeFarField();
      }
      atlasRoot.rotation.x = EXTERNAL_TILT;
    } else {
      atlasRoot.position.set(0, 0, 0);
      atlasRoot.rotation.x = 0;
      world.worldEpochJd = null;
      worldShapes.clear();
      // The proxy pose below borrowed the drawn frame's projection; the focus
      // rig's own is this camera's aspect and nothing else.
      camera.updateProjectionMatrix();
      subtree.activate();
      deps.live.needsRender = true;
    }
  }

  /** A shape mesh for every body drawn large enough to show one. */
  function requestWorldShapes(): void {
    const orrery = deps.bodyScene.state.orrery;
    if (!orrery || !deps.bodyScene.state.shapeGlobes || deps.injects.state.shapeSphereDebug) return;
    for (const b of orrery.bodies) {
      const deg = b.mesh.userData['trueAngularDiameterDeg'];
      if (typeof deg !== 'number' || deg < WORLD_SHAPE_MIN_DEG) continue;
      if (worldShapes.has(b.row.id)) continue;
      worldShapes.add(b.row.id);
      deps.bodyScene.state.shapeGlobes?.request(b.row.id);
    }
  }

  /**
   * The body cruise draws in full: its air, its rings and its own shape.
   *
   * The scattering shell and the shape mesh are the Atlas's, mounted for the
   * body its own ladder is on, so cruise names that body rather than running
   * the focus rig at it. This moves `state.bodyId` and the globe the chunks
   * hang on and nothing else - no camera, no tier, no tile.
   */
  function setWorldBody(id: string | null, look = 1): void {
    const orrery = deps.bodyScene.state.orrery;
    if (deps.live.disposed || !world.externalWorld || !orrery) return;
    atmosphereView.setLook(look);
    if (world.worldBodyId === id) return;
    world.worldBodyId = id;
    bodyReaders.requestSets(id);
    const was = deps.bodyScene.state.bodyNode;
    if (id !== null) {
      state.bodyId = id;
      deps.bodySwitch.state.appliedBodyId = id;
      deps.bodyScene.state.bodyNode = orrery.byId(id) ?? null;
    }
    if (was && was.mesh !== deps.bodyScene.state.bodyNode?.mesh) {
      globeMaterial.clearGlobeShader(was.mesh.material as THREE.MeshStandardMaterial, was.coverage);
    }
    // `syncAtmoMount` reads `ladderBody()`, so the body is named first; it
    // takes the departing body's tables down either way.
    // state.bodyId does not move on a null id, so nothing downstream gives the
    // cruise tree back unless this releases it here.
    if (id === null) { atmosphereView.unmountAtmo(); pyramidView.releaseTiles(); }
    else atmosphereView.syncAtmoMount();
    if (id !== null && deps.bodyScene.state.bodyNode) {
      globeMaterial.installGlobeShader(
        deps.bodyScene.state.bodyNode.mesh.material as THREE.MeshStandardMaterial, deps.bodyScene.state.bodyNode.coverage,
      );
    }
    deps.live.needsRender = true;
  }

  /** Re-host this mount into another element, and put it back on teardown. */
  function adopt(pane: HTMLElement): () => void {
    const from = root.parentElement ?? bootHost;
    const cameFromCruise = world.externalWorld;
    pane.appendChild(root);
    setExternalWorld(false);
    resize();
    // Opened from the bridge, the view opens where the ship is at the
    // heliocentric zoom: the opening fit's own distance, anchored on the body
    // the ship is at rather than on the Sun. Opened any other way it keeps the
    // state the last visit left.
    const focusRow = cameFromCruise && world.shipFocusId ? bodyReaders.rowById(world.shipFocusId) : null;
    if (focusRow && deps.bodyScene.state.orrery) {
      fitOpening(cameraRig.aspect());
      focusCam.goTo(focusRow.id, null, null, focusCam.state.altitudeM);
      state.bodyId = focusRow.id;
      deps.live.cameraDirty = true;
      syncTierToAltitude();
      applyState({ reframe: false });
    }
    deps.live.needsRender = true;
    let given = false;
    return () => {
      if (given) return;
      given = true;
      from.appendChild(root);
      setExternalWorld(true);
      resize();
    };
  }

  scene.userData['atlasWorld'] = {
    get external(): boolean { return world.externalWorld; },
    /** What cruise's ground gate last measured the camera's height as, metres. */
    get cruiseGroundHeightM(): number | null { return world.cruiseGroundHeightM; },
    /** The registry is a fetch, so the bodies arrive a frame or two after the mount. */
    get ready(): boolean { return deps.bodyScene.state.orrery !== null; },
    setExternal(on: boolean): void { setExternalWorld(on); },
    /** The body under the ship, read when the Solar System view opens. */
    setShipFocus(id: string | null): void { world.shipFocusId = id; bodyReaders.requestSets(id); },
    /** The body cruise draws in full, or null for none: the dominant pull. */
    setWorldBody(id: string | null, look?: number): void { setWorldBody(id, look); },
    /**
     * A body the SIM names, rather than a body a view is on.
     *
     * The demand path is keyed on the view and the readers are keyed on the
     * ship and the plan, and those diverge: a flight target is offered by
     * range from anywhere, so a transfer can be aimed at a row no view has
     * named. It goes through the Atlas rather than straight to
     * `ensureOsculating` because a ring already sampled from the secular fit
     * has to be re-sampled when the sets land.
     */
    requestBodySets(id: string | null): void { bodyReaders.requestSets(id); },
    /**
     * The radius the globe is DRAWN at along one direction out of a body's
     * centre, metres at the body's own size however far the camera is, or null
     * off a body with no mesh in the scene and on a shape-model body until its
     * shape has loaded.
     *
     * The mesh carries the body's own relief and its own spin, so this is the
     * surface a ship lands on; the registry's mean `radius_m` is only what the
     * sim's own sphere sweep can meet. `dir` is in the sim's ecliptic axes.
     */
    drawnSurfaceRadiusM(id: string, dir: WorldVec): number | null {
      return surfaceRadius.drawnSurfaceRadiusM(id, dir);
    },
    /** The same surface under a ship resting on the body, cached while it rests. */
    restingGroundRadiusM(id: string, dir: WorldVec): number | null {
      return surfaceRadius.restingGroundRadiusM(id, dir);
    },
    /**
     * The drawn surface along one direction on a body baked from a shape model,
     * metres at the body's own size, or null on one drawn as its datum sphere
     * and on one whose height field has not arrived. The sim's contact law asks
     * this per substep, so it is the field rather than a ray at the mesh.
     */
    shapeSurfaceRadiusM(id: string, dir: WorldVec): number | null {
      const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
      if (node === null) return null;
      return surfaceRadius.shapeSurfaceRadiusSimM(node, dir.x, dir.y, dir.z);
    },
    /** The radius nothing of that shape stands past, metres, or null alongside
     *  a `shapeSurfaceRadiusM` that has nothing to answer with. */
    shapeOuterRadiusM(id: string): number | null {
      return surfaceRadius.shapeOuterRadiusM(id);
    },
    /**
     * The same on a body drawn as a sphere with relief (Luna, Mars): the drawn
     * globe's own facet under the direction, cheap enough for the contact law.
     * Null on a shape-model body and while that globe is a plain sphere.
     */
    reliefSurfaceRadiusM(id: string, dir: WorldVec): number | null {
      return surfaceRadius.reliefSurfaceRadiusM(id, dir);
    },
    /** The radius nothing of that globe stands past, metres, or null beside it. */
    reliefOuterRadiusM(id: string): number | null {
      return surfaceRadius.reliefOuterRadiusM(id);
    },
    /**
     * DEV only, `ground-contact-check.mjs --url`'s truth for the spin lead: the
     * drawn mesh's world matrix, column-major, as it stands at the epoch it
     * was drawn for (`jdTdb` null), or with its rotation put at `jdTdb` by
     * `bodyFixedQuaternion`, the call the orrery poses it with, and put back
     * after. Null where it is not drawn.
     */
    meshWorldAt: import.meta.env.DEV
      ? (id: string, jdTdb: number | null): { m: number[]; jd: number } | null => {
        const mesh = deps.bodyScene.state.orrery?.byId(id)?.mesh ?? null;
        if (mesh === null) return null;
        if (jdTdb === null) {
          mesh.updateWorldMatrix(true, false);
          return { m: mesh.matrixWorld.toArray(), jd: world.worldEpochJd ?? gameClock().jdTdb() };
        }
        const held = mesh.quaternion.clone();
        const posed = bodyFixedQuaternion(deps.bodyScene.state.orrery!.byId(id)!.row, jdTdb, mesh.quaternion);
        mesh.updateWorldMatrix(true, false);
        const m = mesh.matrixWorld.toArray();
        mesh.quaternion.copy(held);
        mesh.updateWorldMatrix(true, false);
        return posed ? { m, jd: jdTdb } : null;
      }
      : undefined,
    /** Width in texels of the globe map a body is drawn with, or null until it lands. */
    globeMapWidth(id: string): number | null {
      const mesh = deps.bodyScene.state.orrery?.byId(id)?.mesh ?? null;
      const map = mesh === null ? null : (mesh.material as THREE.MeshStandardMaterial).map;
      const w = map?.image?.width;
      return typeof w === 'number' && w > 0 ? w : null;
    },
    /**
     * Draw the ship's own reticle in cruise. Off by default: the ship is a few
     * metres from the eye and a marker over something already on screen says
     * nothing. On while the grounded camera stands off, where the hull is
     * under a pixel wide and the reticle is the only thing saying where it is.
     */
    setShipMarkerVisible(on: boolean): void { world.cruiseShipMarker = on === true; },
    /**
     * `orbit-rings --rings-epoch`: what the orbit rings' own rebuild pass has
     * cost since the last read, null until the registry lands. Reading resets
     * the window.
     */
    readRingRebuild(): ReturnType<OrreryHandle['readRingRebuild']> | null {
      return deps.bodyScene.state.orrery ? deps.bodyScene.state.orrery.readRingRebuild() : null;
    },
    /**
     * Put a node in the Atlas's own camera-relative frame, beside the orbit
     * rings, and hand back the closure that takes it out again. The sim's
     * predicted trajectory line comes in this way rather than being built
     * here: it is the ship's own path, not the Atlas's.
     */
    attach(node: THREE.Object3D, at: () => WorldVec): () => void {
      return worldFrame.register(node, at);
    },
    /**
     * The epoch cruise draws the bodies at: the ship's own, or null for the
     * clock. The bodies stand there from this call, so a read of the drawn
     * shape before the frame is drawn reads the epoch it will be drawn at.
     */
    setWorldEpoch(jd: number | null): void {
      world.worldEpochJd = jd !== null && Number.isFinite(jd) ? jd : null;
      deps.bodyScene.state.orrery?.propagate();
    },
    /**
     * One frame: the camera's heliocentric metres in the sim's own ecliptic
     * axes, and where that camera sits in the scene.
     */
    update(camWorld: WorldVec, sceneX: number, sceneY: number, sceneZ: number,
      drawCamera: THREE.Camera | null): void {
      if (deps.live.disposed || !world.externalWorld) return;
      const eye = eclipticToScene(camWorld, 1);
      externalCamWorld.x = eye.x;
      externalCamWorld.y = eye.y;
      externalCamWorld.z = eye.z;
      // Everything the Atlas draws hangs off the subtree, so one position puts
      // the stars, the sun, its light and the camera-relative frame where the
      // sim's camera is instead of at the scene origin.
      atlasRoot.position.set(sceneX, sceneY, sceneZ);
      // The frame is drawn with the sim's camera, so this one is posed as its
      // proxy in the Atlas's own axes: the sky quad rebuilds its view ray from
      // a projection and a basis, and both have to be the drawn frame's. The
      // position stays at the origin, which is where the camera-relative frame
      // puts it.
      if (drawCamera) {
        camera.projectionMatrix.copy(drawCamera.projectionMatrix);
        camera.projectionMatrixInverse.copy(drawCamera.projectionMatrixInverse);
        atlasRoot.getWorldQuaternion(_extRootQ).invert();
        camera.quaternion.copy(_extRootQ.multiply(drawCamera.getWorldQuaternion(_extCamQ)));
        camera.position.set(0, 0, 0);
        camera.updateMatrixWorld(true);
        if ((drawCamera as THREE.PerspectiveCamera).isPerspectiveCamera) {
          world.externalFovDeg = (drawCamera as THREE.PerspectiveCamera).fov;
        }
      }
      const orrery = deps.bodyScene.state.orrery;
      if (!orrery) return;
      readCameraWorld();
      if (!ladder.orreryOn) ladder.setOrreryVisible(true);
      // Per frame rather than at the switch: the registry is a fetch, so on the
      // boot mount the orrery does not exist yet when cruise asks for it.
      orrery.setNamesVisible(false);
      orrery.setRingsVisible(false);
      setSunScale(1);
      orrery.update(cameraWorld);
      // Cruise draws the ship itself, a few metres from the eye. A reticle over
      // it would be a marker for something already on screen.
      deps.bodyScene.state.shipMarker?.setBandVisible(world.cruiseShipMarker);
      deps.bodyScene.state.shipMarker?.update(cameraWorld);
      deps.bodyScene.state.belts?.setBandVisible(false);
      deps.bodyScene.state.belts?.update();
      orrery.layoutLabels(camera, dojoPaints.labelViewportH());
      requestWorldShapes();
      // The ground under a ship flying low. After the orrery, which is what
      // writes this body's centre and rotation this frame, and before the
      // camera-relative pass, which is what puts the pyramid where they say.
      const nowMs = performance.now();
      const tileDt = world.externalTileMs === 0 ? 0 : Math.min((nowMs - world.externalTileMs) / 1000, 0.1);
      world.externalTileMs = nowMs;
      pyramidView.syncCruiseGround();
      pyramidView.updateTiles(tileDt);
      worldFrame.applyCameraRelative(cameraWorld);
      // Cruise streams now, so the scattering pass yields to a tile build here
      // the way the ladder's own frame does.
      atmosphereView.updateAtmoFrame(pyramidView.tileMountedThisFrame === 0);
    },
  };
  own(() => { delete scene.userData['atlasWorld']; });

  return { linearHex, setExternalWorld, requestWorldShapes, setWorldBody, adopt };
}
